import { all, put, takeEvery, fork, select } from "redux-saga/effects";
import { apiURL } from "../actions/helpers";
import axios from "axios";
import moment from "moment";
import { SOCKET_MESSAGE } from "./socket";
import orderBy from "lodash/orderBy";
import clone from "lodash/clone";
import { v4 as uuidv4 } from "uuid";

// Action Types
export const LIVE_OPTIONS_LOAD = "LOF/LOAD/TOP";
export const LIVE_OPTIONS_LOAD_SUCCESS = "LOF/LOAD/TOP/SUCCESS";
export const LIVE_OPTIONS_LOAD_FAILURE = "LOF/LOAD/TOP/FAILURE";
export const LIVE_OPTIONS_LOAD_MORE = "LOF/LOAD/MORE"; // New action for infinite scroll

// Constants
const MAX_CACHED_PAGES = 3;
const DEFAULT_PAGE_SIZE = 100;
const MAX_INFINITE_SCROLL_ITEMS = 1000; // Limit infinite scroll to prevent memory issues

// Action Creators
export const liveOptionsLoad = (
  prem_min = 35000,
  exp_within = 730,
  loadMore = false,
  date = "",
  tickers = "",
  clearCache = false,
  sortModel = {
    field: "time",
    sort: "desc",
  },
  bidAskFilter = [],
  contract_quantity_min = 0,
  exclude = "",
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  price_per_contract_min = 0.1,
) => ({
  type: LIVE_OPTIONS_LOAD,
  tickers,
  prem_min,
  exp_within,
  loadMore,
  date,
  clearCache,
  sortModel,
  bidAskFilter,
  contract_quantity_min,
  exclude,
  page,
  pageSize,
  price_per_contract_min,
});

// New action for infinite scroll
export const liveOptionsLoadMore = (
  prem_min = 35000,
  exp_within = 730,
  date = "",
  tickers = "",
  sortModel = {
    field: "time",
    sort: "desc",
  },
  bidAskFilter = [],
  contract_quantity_min = 0,
  exclude = "",
  pageSize = DEFAULT_PAGE_SIZE,
  price_per_contract_min = 0.1,
) => ({
  type: LIVE_OPTIONS_LOAD_MORE,
  tickers,
  prem_min,
  exp_within,
  date,
  sortModel,
  bidAskFilter,
  contract_quantity_min,
  exclude,
  pageSize,
  price_per_contract_min,
});

export const liveOptionsLoadSuccess = (data) => ({
  type: LIVE_OPTIONS_LOAD_SUCCESS,
  data,
});

export const liveOptionsLoadFailure = (error) => ({
  type: LIVE_OPTIONS_LOAD_FAILURE,
  error,
});

// Helper function to implement LRU cache eviction
const evictOldPages = (pages, maxPages) => {
  const pageNumbers = Object.keys(pages)
    .map(Number)
    .sort((a, b) => b - a);
  if (pageNumbers.length <= maxPages) return pages;

  const newPages = {};
  pageNumbers.slice(0, maxPages).forEach((pageNum) => {
    newPages[pageNum] = pages[pageNum];
  });

  return newPages;
};

// Helper function to get current page rows
const getCurrentPageRows = (pages, currentPage) => {
  return pages[currentPage] || [];
};

// Sagas
function* fetchLiveOptions(action) {
  try {
    const {
      pages,
      date: dateRedux,
      prem_min: prem_min_redux,
      exp_within: exp_within_redux,
      sortModel: sortModelRedux,
      contract_quantity_min: contract_quantity_min_redux,
      price_per_contract_min: price_per_contract_min_redux,
    } = yield select((state) => state.newLiveOptions);

    const newLiveOptionsState = yield select((state) => state.newLiveOptions);

    const {
      prem_min,
      exp_within,
      date,
      tickers,
      clearCache,
      sortModel,
      bidAskFilter,
      contract_quantity_min,
      exclude,
      page,
      pageSize,
      price_per_contract_min,
    } = action;

    yield put(
      liveOptionsLoadSuccess({
        ...newLiveOptionsState,
        clearCache,
        loading: true,
        sortModel,
        bidAskFilter,
        contract_quantity_min,
        exclude,
        currentPage: page,
        pageSize,
        price_per_contract_min,
      }),
    );

    if (!clearCache) {
      const diffDates = dateRedux !== date;
      const diffPrem = prem_min !== prem_min_redux;
      const diffExp =
        JSON.stringify(exp_within) !== JSON.stringify(exp_within_redux);
      const diffContractMin =
        JSON.stringify(contract_quantity_min) !==
        JSON.stringify(contract_quantity_min_redux);
      const diffSortModel =
        JSON.stringify(sortModel) !== JSON.stringify(sortModelRedux);
      const diffTickers = newLiveOptionsState.tickers !== tickers;
      const diffExclude = newLiveOptionsState.exclude !== exclude;
      const diffPricePerContract =
        price_per_contract_min !== price_per_contract_min_redux;

      // If filters changed, clear cache and start fresh
      if (
        diffDates ||
        diffPrem ||
        diffExp ||
        diffSortModel ||
        diffContractMin ||
        diffTickers ||
        diffExclude ||
        diffPricePerContract
      ) {
        yield put(
          liveOptionsLoadSuccess({
            clearCache,
            pages: {},
            infiniteScrollData: [], // Clear infinite scroll data too
            currentPage: page,
            pageSize,
            tickers,
            prem_min,
            exp_within,
            loadMore: false,
            date,
            summary: {},
            loading: true,
            sortModel,
            bidAskFilter,
            contract_quantity_min,
            exclude,
            price_per_contract_min,
            totalRowCount: 0,
            hasMore: true,
            lastRequestedTime: null, // Reset timestamp tracking
            lastRequestedValue: null, // Reset value tracking
            lastRequestedId: null, // Reset ID tracking
            lastApiCursor: null, // Reset API cursor
            lastFetchedPage: null, // Reset fetched page
            isRequestInProgress: false, // Reset request tracking
          }),
        );
      }

      // Check if page is already cached
      if (
        pages[page] &&
        !diffDates &&
        !diffPrem &&
        !diffExp &&
        !diffSortModel &&
        !diffContractMin &&
        !diffTickers &&
        !diffExclude &&
        !diffPricePerContract
      ) {
        yield put(
          liveOptionsLoadSuccess({
            ...newLiveOptionsState,
            currentPage: page,
            loading: false,
          }),
        );
        return;
      }
      const baFilter = bidAskFilter.length ? bidAskFilter.join(",") : "";
      let uri = `${apiURL}/darkpool/options?limit=${pageSize}&prem_min=${prem_min}&exp_within=${exp_within}&order_direction=${sortModel.sort.toUpperCase()}&sort_column=${sortModel.field}&bid_ask=${baFilter}&contract_quantity_min=${contract_quantity_min}&price_per_contract_min=${price_per_contract_min}`;
      let snapshotUri = `${apiURL}/darkpool/snapshot?prem_min=${prem_min}&exp_within=${exp_within}&contract_quantity_min=${contract_quantity_min}&price_per_contract_min=${price_per_contract_min}`;

      if (tickers.length) {
        uri += `&ticker=${tickers}`;
        snapshotUri += `&ticker=${tickers}`;
      }

      if (exclude.length) {
        uri += `&exclude=${exclude}`;
        snapshotUri += `&exclude=${exclude}`;
      }

      if (date.length) {
        uri += `&date=${date}`;
        snapshotUri += `&date=${date}`;
      }

      const [response, snapshotResponse] = yield Promise.all([
        axios.get(uri, { withCredentials: true }),
        axios.get(snapshotUri, { withCredentials: true }),
      ]);

      let data = [...response.data];

      // Mark all API data as non-socket data
      data = data.map((item) => ({
        ...item,
        isSocketData: false,
      }));

      if (date.length) {
        const [startDate, endDate] = date.split(",");
        data = data.filter(
          (item) =>
            moment(Number(item.time) * 1000).isSameOrAfter(startDate, "day") &&
            moment(Number(item.time) * 1000).isSameOrBefore(endDate, "day"),
        );
      }

      // Update pages with new data and implement LRU eviction
      const updatedPages = { ...pages, [page]: data };
      const evictedPages = evictOldPages(updatedPages, MAX_CACHED_PAGES);

      // For infinite scroll: if this is page 1 or filters changed, also populate infiniteScrollData
      let infiniteScrollData = newLiveOptionsState.infiniteScrollData;
      let lastFetchedPage = data.length > 0 ? [...data] : null;
      if (
        page === 1 ||
        diffDates ||
        diffPrem ||
        diffExp ||
        diffSortModel ||
        diffContractMin ||
        diffPricePerContract
      ) {
        // Start fresh for infinite scroll
        infiniteScrollData = [...data];
        // Sort the initial data properly
        infiniteScrollData = orderBy(
          infiniteScrollData,
          [sortModel.field, "id"],
          [sortModel.sort, sortModel.sort],
        );
      }

      // Calculate total row count - try multiple sources
      let totalRowCount = 0;

      // Method 1: Check response headers for total count
      if (response.headers["x-total-count"]) {
        totalRowCount = parseInt(response.headers["x-total-count"]);
      }
      // Method 2: Check if response has totalCount property
      else if (
        response.data &&
        typeof response.data === "object" &&
        response.data.totalCount
      ) {
        totalRowCount = response.data.totalCount;
        data = response.data.data || response.data.rows || [];
      }
      // Method 3: Check snapshot response for total count
      else if (snapshotResponse.data.total_count) {
        totalRowCount = snapshotResponse.data.total_count;
      }
      // Method 4: Fallback - estimate based on page data
      else {
        // If we got a full page, assume there might be more
        if (data.length >= pageSize) {
          // Estimate: if we're on page N and got full results, assume there are at least N+1 pages worth
          totalRowCount = (page + 1) * pageSize;
        } else {
          // If we got less than a full page, this is likely the last page
          totalRowCount = (page - 1) * pageSize + data.length;
        }
      }

      // Determine if there's more data for infinite scroll
      const hasMore = data.length >= pageSize;

      yield put(
        liveOptionsLoadSuccess({
          clearCache,
          pages: evictedPages,
          infiniteScrollData,
          currentPage: page,
          pageSize,
          tickers,
          prem_min,
          exp_within,
          loadMore: false,
          date,
          summary: snapshotResponse.data,
          loading: false,
          sortModel,
          bidAskFilter,
          contract_quantity_min,
          exclude,
          price_per_contract_min,
          totalRowCount,
          hasMore,
          lastRequestedTime: null, // Reset timestamp tracking
          lastApiCursor: null, // Reset API cursor
          lastFetchedPage, // Store the page we just fetched
          isRequestInProgress: false, // Reset request tracking
        }),
      );
    } else {
      yield put(
        liveOptionsLoadSuccess({
          clearCache,
          pages: {},
          infiniteScrollData: [], // Clear infinite scroll data too
          currentPage: page,
          pageSize,
          tickers,
          prem_min,
          exp_within,
          loadMore: false,
          date,
          summary: {},
          loading: false,
          sortModel,
          bidAskFilter,
          contract_quantity_min,
          exclude,
          price_per_contract_min,
          totalRowCount: 0,
          hasMore: true,
          lastRequestedTime: null, // Reset timestamp tracking
          lastApiCursor: null, // Reset API cursor
          lastFetchedPage: null, // Reset fetched page
          isRequestInProgress: false, // Reset request tracking
        }),
      );
    }
  } catch (error) {
    yield put(liveOptionsLoadFailure(error));
  }
}

function* fetchLiveOptionsMore(action) {
  try {
    const state = yield select((state) => state.newLiveOptions);
    const {
      infiniteScrollData,
      loading,
      lastRequestedTime = null,
      lastRequestedValue = null,
      lastRequestedId = null,
      isRequestInProgress = false,
      lastFetchedPage = null,
      sortModel: _currentSortModel,
    } = state;

    // Don't load more if already loading or request in progress
    if (loading || isRequestInProgress) {
      return;
    }

    // Don't load more if we don't have any initial data
    if (!lastFetchedPage || lastFetchedPage.length === 0) {
      return;
    }

    const {
      prem_min,
      exp_within,
      date,
      tickers,
      sortModel,
      bidAskFilter,
      contract_quantity_min,
      exclude,
      pageSize,
      price_per_contract_min,
    } = action;

    // Determine which item to use as cursor based on sort order
    let lastApiItem;
    if (sortModel.sort === "desc") {
      // For descending sort, use the last item (smallest value)
      lastApiItem = lastFetchedPage[lastFetchedPage.length - 1];
    } else {
      // For ascending sort, also use the last item (largest value so far)
      lastApiItem = lastFetchedPage[lastFetchedPage.length - 1];
    }

    // Prevent duplicate requests for the same cursor
    if (sortModel.field === "time" && lastApiItem.time === lastRequestedTime) {
      return;
    } else if (
      sortModel.field !== "time" &&
      lastApiItem[sortModel.field] === lastRequestedValue &&
      lastApiItem.id === lastRequestedId
    ) {
      return;
    }

    // Set loading state and track the requested cursor
    yield put(
      liveOptionsLoadSuccess({
        infiniteScrollData,
        loading: true,
        isRequestInProgress: true,
        lastRequestedTime: lastApiItem.time,
        lastRequestedValue: lastApiItem[sortModel.field],
        lastRequestedId: lastApiItem.id,
        sortModel,
        bidAskFilter,
        contract_quantity_min,
        exclude,
        prem_min,
        exp_within,
        date,
        tickers,
        price_per_contract_min,
      }),
    );

    const baFilter = bidAskFilter.length ? bidAskFilter.join(",") : "";

    // Build cursor parameters based on sort field
    let cursorParams = "";
    if (sortModel.field === "time") {
      // For time-based sorting, use the existing last_time parameter
      cursorParams = `&last_time=${lastApiItem.time}`;
    } else {
      // For other fields, use the new cursor parameters
      const lastValue = lastApiItem[sortModel.field];
      const lastId = lastApiItem.id;
      cursorParams = `&last_value=${lastValue}&last_id=${lastId}`;
    }

    // Build the URI with proper cursor parameters
    let uri = `${apiURL}/darkpool/options?limit=${pageSize}&prem_min=${prem_min}&exp_within=${exp_within}&order_direction=${sortModel.sort.toUpperCase()}&sort_column=${sortModel.field}&bid_ask=${baFilter}&contract_quantity_min=${contract_quantity_min}&price_per_contract_min=${price_per_contract_min}${cursorParams}`;

    if (tickers.length) {
      uri += `&ticker=${tickers}`;
    }

    if (exclude.length) {
      uri += `&exclude=${exclude}`;
    }

    if (date.length) {
      uri += `&date=${date}`;
    }

    const response = yield axios.get(uri, { withCredentials: true });

    let newData = [...response.data];

    // Mark all API data as non-socket data
    newData = newData.map((item) => ({
      ...item,
      isSocketData: false,
    }));

    if (date.length) {
      const [startDate, endDate] = date.split(",");
      newData = newData.filter(
        (item) =>
          moment(Number(item.time) * 1000).isSameOrAfter(startDate, "day") &&
          moment(Number(item.time) * 1000).isSameOrBefore(endDate, "day"),
      );
    }

    // Store this page as the new lastFetchedPage
    const newLastFetchedPage =
      newData.length > 0 ? [...newData] : lastFetchedPage;

    // Append new data to existing data
    let updatedData = [...infiniteScrollData, ...newData];

    // Remove duplicates using ID as the unique key
    const seenIds = new Set();
    updatedData = updatedData.filter((item) => {
      if (seenIds.has(item.id)) {
        return false;
      }
      seenIds.add(item.id);
      return true;
    });

    // Important: For cursor-based pagination, we should NOT re-sort all data!
    // The API returns data in the correct order based on the cursor.
    // Re-sorting would mess up the pagination continuity.
    // Only sort if we're dealing with time-based sorting where socket data might be mixed in
    if (sortModel.field === "time") {
      // For time-based sorting, we need to sort because of socket data
      updatedData = orderBy(
        updatedData,
        [sortModel.field, "id"],
        [sortModel.sort, sortModel.sort],
      );
    }
    // For other fields (like ticker), trust the API's ordering from cursor-based pagination

    // Limit total items to prevent memory issues
    if (updatedData.length > MAX_INFINITE_SCROLL_ITEMS) {
      // For cursor-based pagination, keep the most recent items (tail of the array)
      // This ensures continuity when scrolling
      updatedData = updatedData.slice(-MAX_INFINITE_SCROLL_ITEMS);
    }

    const hasMore = newData.length >= pageSize; // If we got a full page, there might be more

    yield put(
      liveOptionsLoadSuccess({
        infiniteScrollData: updatedData,
        loading: false,
        isRequestInProgress: false,
        hasMore,
        lastFetchedPage: newLastFetchedPage, // Update the last fetched page
        sortModel,
        bidAskFilter,
        contract_quantity_min,
        exclude,
        prem_min,
        exp_within,
        date,
        tickers,
        price_per_contract_min,
      }),
    );
  } catch (error) {
    // Reset loading states on error
    yield put(
      liveOptionsLoadSuccess({
        loading: false,
        isRequestInProgress: false,
      }),
    );
    yield put(liveOptionsLoadFailure(error));
  }
}

function* listenLiveOptionsLoad() {
  yield takeEvery(LIVE_OPTIONS_LOAD, fetchLiveOptions);
}

function* listenLiveOptionsLoadMore() {
  yield takeEvery(LIVE_OPTIONS_LOAD_MORE, fetchLiveOptionsMore);
}

/**
 * Ensure that a given item is in array format
 * @param {string | array} item
 * @returns {Array.<string>}
 */
function ensureArrayFormat(item) {
  if (typeof item === "string" && item.includes(",")) {
    return item.split(",");
  }
  if (typeof item === "string" && item.length) {
    return [item];
  }
  return item;
}

// Helper function to check if a new option should be merged into active pages
const shouldMergeIntoPage = (
  option,
  pageData,
  currentPage,
  pageSize,
  sortField,
  sortOrder,
) => {
  if (!pageData || pageData.length === 0) return currentPage === 1;

  // Only merge into page 1 for real-time data (most recent page)
  if (currentPage !== 1) return false;

  // Check if the new option is more recent than the oldest item in page 1
  const sortedPageData = orderBy(pageData, [sortField], [sortOrder]);
  const oldestInPage = sortedPageData[sortedPageData.length - 1];

  if (sortField === "time") {
    return Number(option.time) > Number(oldestInPage.time);
  }

  return true; // For other sort fields, always merge for now
};

// Root Saga
export function* saga() {
  yield all([fork(listenLiveOptionsLoad), fork(listenLiveOptionsLoadMore)]);
}

const INIT_STATE = {
  pages: {}, // { [pageNumber]: [...rows] } - for pagination mode
  infiniteScrollData: [], // [...rows] - for infinite scroll mode
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalRowCount: 0,
  hasMore: true, // for infinite scroll
  lastRequestedTime: null, // Track last timestamp for cursor-based pagination
  lastRequestedValue: null, // Track last value for sort field cursor
  lastRequestedId: null, // Track last ID for stable cursor
  lastApiCursor: null, // Store the last successful API cursor
  lastFetchedPage: null, // Track the last page of data we fetched from API
  isRequestInProgress: false, // Prevent multiple simultaneous requests
  tickers: "",
  exclude: "",
  prem_min: 35000,
  exp_within: 730,
  contract_quantity_min: 0,
  price_per_contract_min: 0.1,
  loadMore: false,
  date: "",
  summary: {
    put_count: 0,
    call_count: 0,
    call_flow: 0,
    put_flow: 0,
    flow_sentiment: 0.5,
  },
  loading: true,
  clearCache: false,
  sortModel: {
    field: "time",
    sort: "desc",
  },
  bidAskFilter: [],
};

// Reducer
const reducer = (state = INIT_STATE, action) => {
  switch (action.type) {
    case SOCKET_MESSAGE: {
      try {
        // Only update state if the message is related to options
        if (!action.channel.includes("options")) {
          return state;
        }

        // If the user is looking at historic options, don't update the state
        const lookingAtHistoricOptions =
          state.date.length > 0 &&
          moment(state.date.split(",")[1]).isBefore(
            moment.utc().format("YYYY-MM-DD"),
          );
        if (lookingAtHistoricOptions) {
          return state;
        }

        if (state.clearCache) {
          return { ...state, pages: {}, infiniteScrollData: [] };
        }

        const newOptions = action.data;
        let updatedPages = { ...state.pages };
        let updatedInfiniteScrollData = [...state.infiniteScrollData];
        let newSummary = clone(state.summary);

        // Only merge real-time events into active pages (primarily page 1)
        newOptions.forEach((option) => {
          // Apply filters to determine if this option should be included
          if (state.tickers.length && !state.tickers.includes(option.ticker)) {
            return;
          }
          if (state.exclude.length && state.exclude.includes(option.ticker)) {
            return;
          }
          if (state.prem_min && Number(option.prem) < state.prem_min) {
            return;
          }
          if (state.exp_within.length) {
            let [minExp, maxExp] = state.exp_within;
            let minExpiryDate = moment.utc().add(minExp, "days").startOf("day");
            let maxExpiryDate = moment.utc().add(maxExp, "days").endOf("day");
            if (
              !moment.utc(option.exp).isSameOrAfter(minExpiryDate, "day") ||
              !moment.utc(option.exp).isSameOrBefore(maxExpiryDate, "day")
            ) {
              return;
            }
          }

          if (state.bidAskFilter.length) {
            const { ask_price, bid_price, price_per_contract } = option;
            const fillLookup = {
              "": "",
              1: ask_price < price_per_contract, // Above Ask
              2: ask_price === price_per_contract, // At Ask
              3:
                ask_price - price_per_contract < price_per_contract - bid_price, // Lean Ask
              4: bid_price > price_per_contract, // Below Bid
              5: bid_price === price_per_contract, // At bid
              6:
                ask_price - price_per_contract > price_per_contract - bid_price, // Lean Bid
              7:
                ask_price - price_per_contract ===
                price_per_contract - bid_price, // In Between
            };
            if (!state.bidAskFilter.some((i) => fillLookup[i])) {
              return;
            }
          }

          if (
            state.contract_quantity_min > 0 &&
            option.contract_quantity < state.contract_quantity_min
          ) {
            return;
          }

          if (
            state.price_per_contract_min > 0 &&
            Number(option.price_per_contract) < state.price_per_contract_min
          ) {
            return;
          }

          if (!option.id) {
            option.newItem = true;
            option.newItemTimestamp = Date.now(); // Add timestamp to track when item was marked as new
            option.id = uuidv4();
            option.isSocketData = true; // Mark as socket data to exclude from cursor
            if (option.cp === "P") {
              newSummary.put_count++;
              newSummary.put_total_prem += parseInt(option.prem);
            } else {
              newSummary.call_count++;
              newSummary.call_total_prem += parseInt(option.prem);
            }
          } else {
            // If it has an ID, it's from the database
            option.isSocketData = false;
          }

          // Update pagination pages
          Object.keys(updatedPages).forEach((pageNum) => {
            const pageNumber = Number(pageNum);
            const pageData = updatedPages[pageNumber];

            if (
              shouldMergeIntoPage(
                option,
                pageData,
                pageNumber,
                state.pageSize,
                state.sortModel.field,
                state.sortModel.sort,
              )
            ) {
              // Add to page and maintain page size limit
              let newPageData = [...pageData, option];

              // Remove duplicates and sort
              newPageData = [
                ...new Map(
                  newPageData.map((item) => [item["id"], item]),
                ).values(),
              ];
              newPageData = orderBy(
                newPageData,
                [state.sortModel.field],
                [state.sortModel.sort],
              );

              // Trim to page size if necessary (remove oldest items)
              if (newPageData.length > state.pageSize) {
                newPageData = newPageData.slice(0, state.pageSize);
              }

              updatedPages[pageNumber] = newPageData;
            }
          });

          // Update infinite scroll data - add to beginning for newest first
          // BUT be careful not to interfere with cursor-based pagination
          const isLoadingMoreData = state.isRequestInProgress || state.loading;

          if (!isLoadingMoreData) {
            // Only add socket data if we're not currently loading more data
            // Add based on sort order
            if (state.sortModel.field === "time") {
              if (state.sortModel.sort === "desc") {
                // For time descending (newest first), add to beginning
                updatedInfiniteScrollData.unshift(option);
              } else {
                // For time ascending (oldest first), add to end
                updatedInfiniteScrollData.push(option);
              }
            } else {
              // For other sorts, append and will re-sort
              updatedInfiniteScrollData.push(option);
            }

            // Remove duplicates using ID as the unique key
            const seenIds = new Set();
            updatedInfiniteScrollData = updatedInfiniteScrollData.filter(
              (item) => {
                if (seenIds.has(item.id)) {
                  return false;
                }
                seenIds.add(item.id);
                return true;
              },
            );

            // Sort with ID as secondary sort for stability
            updatedInfiniteScrollData = orderBy(
              updatedInfiniteScrollData,
              [state.sortModel.field, "id"],
              [state.sortModel.sort, state.sortModel.sort],
            );

            // Limit total items to prevent memory issues
            if (updatedInfiniteScrollData.length > MAX_INFINITE_SCROLL_ITEMS) {
              updatedInfiniteScrollData = updatedInfiniteScrollData.slice(
                0,
                MAX_INFINITE_SCROLL_ITEMS,
              );
            }
          }
        });

        // Update summary calculations
        const putToCallRatio = newSummary.put_count / newSummary.call_count;
        newSummary.put_to_call = Math.round(putToCallRatio * 1000) / 1000;
        const optionsCount = newSummary.call_count + newSummary.put_count;
        newSummary.call_flow = newSummary.call_count / optionsCount;
        newSummary.flow_sentiment = newSummary.call_count / optionsCount;
        newSummary.put_flow = newSummary.put_count / optionsCount;

        return {
          ...state,
          pages: updatedPages,
          infiniteScrollData: updatedInfiniteScrollData,
          summary: newSummary,
        };
      } catch (_error) {
        return state;
      }
    }

    case LIVE_OPTIONS_LOAD_SUCCESS: {
      let {
        tickers,
        prem_min,
        exp_within,
        date,
        loadMore,
        pages,
        infiniteScrollData,
        currentPage,
        pageSize,
        summary,
        loading,
        clearCache,
        sortModel,
        bidAskFilter,
        contract_quantity_min,
        exclude,
        totalRowCount,
        hasMore,
        lastRequestedTime,
        lastRequestedValue,
        lastRequestedId,
        lastApiCursor,
        lastFetchedPage,
        isRequestInProgress,
        price_per_contract_min,
      } = action.data;

      tickers = ensureArrayFormat(tickers);
      exclude = ensureArrayFormat(exclude);

      return {
        ...state,
        pages: pages !== undefined ? pages : state.pages,
        infiniteScrollData:
          infiniteScrollData !== undefined
            ? infiniteScrollData
            : state.infiniteScrollData,
        currentPage: currentPage || state.currentPage,
        pageSize: pageSize || state.pageSize,
        totalRowCount:
          totalRowCount !== undefined ? totalRowCount : state.totalRowCount,
        hasMore: hasMore !== undefined ? hasMore : state.hasMore,
        lastRequestedTime:
          lastRequestedTime !== undefined
            ? lastRequestedTime
            : state.lastRequestedTime,
        lastRequestedValue:
          lastRequestedValue !== undefined
            ? lastRequestedValue
            : state.lastRequestedValue,
        lastRequestedId:
          lastRequestedId !== undefined
            ? lastRequestedId
            : state.lastRequestedId,
        lastApiCursor:
          lastApiCursor !== undefined ? lastApiCursor : state.lastApiCursor,
        lastFetchedPage:
          lastFetchedPage !== undefined
            ? lastFetchedPage
            : state.lastFetchedPage,
        isRequestInProgress:
          isRequestInProgress !== undefined
            ? isRequestInProgress
            : state.isRequestInProgress,
        tickers,
        prem_min,
        exp_within,
        date,
        loadMore,
        summary: summary || state.summary,
        loading,
        clearCache,
        sortModel,
        bidAskFilter,
        contract_quantity_min,
        exclude,
        price_per_contract_min:
          price_per_contract_min !== undefined
            ? price_per_contract_min
            : state.price_per_contract_min,
      };
    }

    default:
      return state;
  }
};

// Selectors for Pagination Mode
export const getCurrentPageData = (state) => {
  const { pages, currentPage } = state.newLiveOptions;
  return getCurrentPageRows(pages, currentPage);
};

export const getTotalRowCount = (state) => {
  return state.newLiveOptions.totalRowCount;
};

export const getCurrentPage = (state) => {
  return state.newLiveOptions.currentPage;
};

export const getPageSize = (state) => {
  return state.newLiveOptions.pageSize;
};

// Selectors for Infinite Scroll Mode
export const getInfiniteScrollData = (state) => {
  return state.newLiveOptions.infiniteScrollData;
};

export const getHasMore = (state) => {
  return state.newLiveOptions.hasMore;
};

export const getIsLoading = (state) => {
  return state.newLiveOptions.loading;
};

export default reducer;
